import { useImperativeHandle, useMemo, useRef, useState } from "react";

import { TimeValidationError } from "@mui/x-date-pickers";
import { FieldChangeHandlerContext } from "@mui/x-date-pickers/internals";
import { Dayjs } from "dayjs";

import { UseTimeFieldParams } from "./TimeField.types";

export const useTimeField = <R>({
  ref,
  defaultValue,
  value,
  onChange,
}: UseTimeFieldParams<R>) => {
  const timeFieldRef = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<Dayjs | null>(
    value ?? defaultValue ?? null
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(
    ref,
    (() => {
      return {
        instance: timeFieldRef.current,
        value: state,
        setValue: (v: Dayjs | null) => {
          setState(v);
        },
        reset: () => {
          setState(null);
        },
      };
    }) as unknown as () => R,
    [state]
  );

  const onChangeHandler = (
    newValue: Dayjs | null,
    context: FieldChangeHandlerContext<TimeValidationError>
  ) => {
    if (typeof value === "undefined") {
      setState(newValue);
    }
    onChange?.(newValue, context);
  };

  const valueState = useMemo(
    () => (typeof value !== "undefined" ? value : state),
    [value, state]
  );

  return {
    timeFieldRef,
    valueState,
    onChangeHandler,
  };
};
